export const colorSchemePreferences = ['system', 'light', 'dark'] as const;

export type ColorSchemePreference = (typeof colorSchemePreferences)[number];
export type ResolvedColorScheme = Exclude<ColorSchemePreference, 'system'>;

export const COLOR_SCHEME_STORAGE_KEY = 'cobrainer-color-scheme';
export const DARK_COLOR_SCHEME_QUERY = '(prefers-color-scheme: dark)';

const browserThemeColorFallbacks: Record<ResolvedColorScheme, string> = {
    dark: '#040f16',
    light: '#f9f9f9',
};

const browserThemeColorMetaNames = ['theme-color', 'msapplication-TileColor'] as const;

export const isColorSchemePreference = (value: string | null): value is ColorSchemePreference =>
    value === 'system' || value === 'light' || value === 'dark';

export const readColorSchemePreference = (): ColorSchemePreference => {
    try {
        const preference = globalThis.localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);

        return isColorSchemePreference(preference) ? preference : 'light';
    } catch {
        return 'light';
    }
};

export const readSystemColorScheme = (): ResolvedColorScheme =>
    globalThis.matchMedia(DARK_COLOR_SCHEME_QUERY).matches ? 'dark' : 'light';

export const resolveColorScheme = (
    preference: ColorSchemePreference,
    systemColorScheme: ResolvedColorScheme,
): ResolvedColorScheme => (preference === 'system' ? systemColorScheme : preference);

export const readSemanticColor = (propertyName: `--semantic-${string}`, fallback: string): string => {
    const value = globalThis
        .getComputedStyle(globalThis.document.documentElement)
        .getPropertyValue(propertyName)
        .trim();

    return value.length > 0 ? value : fallback;
};

export const applyColorScheme = (colorScheme: ResolvedColorScheme): void => {
    globalThis.document.documentElement.dataset['colorScheme'] = colorScheme;
    globalThis.document.documentElement.style.colorScheme = colorScheme;
    const browserThemeColor = readSemanticColor('--semantic-surface-canvas', browserThemeColorFallbacks[colorScheme]);

    for (const metaName of browserThemeColorMetaNames) {
        globalThis.document.querySelector(`meta[name="${metaName}"]`)?.setAttribute('content', browserThemeColor);
    }
};

export const initializeColorScheme = (): void => {
    applyColorScheme(resolveColorScheme(readColorSchemePreference(), readSystemColorScheme()));
};

export const writeColorSchemePreference = (preference: ColorSchemePreference): void => {
    try {
        globalThis.localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, preference);
    } catch {
        return;
    }
};
